import { create, ApiResponse } from 'apisauce';
import _ from 'lodash';
import Storage from 'utils/Storage';
import { isSsoEnabled } from 'utils/authMode';

type BuildApiOptions = {
  baseURL: string;
  getToken?: () => string | null;
  handle401?: boolean;
};

const buildBaseApi = ( { baseURL, getToken, handle401 = true }: BuildApiOptions ) => {
  const api = create( {
    baseURL,
    timeout: 600000,
  } );

  const clearAndRedirectToLogin = () => {
    Storage.clear( 'grupoimpar:token' );
    Storage.clear( 'grupoimpar:refreshToken' );
    Storage.clear( 'persist:grupoimpar' );
    const isLoginPage = window.location.pathname === '/login';
    if ( !isLoginPage ) {
      setTimeout( () => {
        window.location.replace( '/login' );
      }, 100 );
    }
  };

  const isSsoAuthEndpoint = ( url = '' ) => (
    url.includes( '/api/authentication/sso/login' )
    || url.includes( '/api/authentication/sso/confirm' )
    || url.includes( '/api/authentication/sso/refresh' )
    || url.includes( '/api/authentication/sso/logout' )
  );

  api.axiosInstance.interceptors.response.use(
    ( response ) => response,
    async ( axiosError:any ) => {
      const originalRequest = axiosError?.config || {};
      const status = axiosError?.response?.status;

      if (
        status === 401
        && handle401
        && isSsoEnabled()
        && !originalRequest?.authRefreshRetried
        && !originalRequest?.skipAuthRefresh
        && !isSsoAuthEndpoint( originalRequest?.url )
      ) {
        const currentRefreshToken = Storage.get( 'grupoimpar:refreshToken' );
        if ( !currentRefreshToken ) {
          clearAndRedirectToLogin();
          return Promise.reject( axiosError );
        }

        originalRequest.authRefreshRetried = true;

        try {
          const refreshResponse = await api.axiosInstance.post(
            'api/authentication/sso/refresh',
            { refreshToken: currentRefreshToken },
            { skipAuthRefresh: true } as any,
          );

          const nextAccessToken = refreshResponse?.data?.accessToken;
          const nextRefreshToken = refreshResponse?.data?.refreshToken;
          if ( !nextAccessToken || !nextRefreshToken ) {
            throw new Error( 'Invalid refresh response' );
          }

          Storage.set( 'grupoimpar:token', nextAccessToken );
          Storage.set( 'grupoimpar:refreshToken', nextRefreshToken );

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;

          return await api.axiosInstance.request( originalRequest );
        } catch ( refreshError ) {
          console.warn( '[SSO] Refresh failed, closing local session.' );
          clearAndRedirectToLogin();
          return Promise.reject( refreshError );
        }
      }

      return Promise.reject( axiosError );
    },
  );

  api.addRequestTransform( ( request ) => {
    const token = getToken?.();
    if ( token ) {
      request.headers.Authorization = `Bearer ${token}`;
    }
  } );

  api.addResponseTransform( ( response: ApiResponse<any> ) => {
    const {
      data, ok, status, config,
    } = response;

    if ( !ok ) {
      let errors: string | Record<string, string> = '';

      if ( status === 401 ) {
        if ( handle401 ) {
          clearAndRedirectToLogin();
          return;
        }
        errors = '401';
      }
      console.log( 'buildBaseApi-data', data );
      if ( data ) {
        if ( data.form?.errors ) {
          const errorsObject: Record<string, string> = {};
          _.map( data.form.errors, ( error, key: string ) => {
            if ( !_.isEmpty( error ) ) {
              errorsObject[key] = error.join( '\n' );
            }
          } );
          errors = errorsObject;
        } else if ( data.errors ) {
          if ( _.isObject( data.errors ) && !_.isArray( data.errors ) ) {
            const arrayErrors: string[] = [];
            _.map( data.errors, ( error ) => {
              if ( _.isString( error ) ) arrayErrors.push( error );
              else if ( _.isArray( error ) ) arrayErrors.push( error.join( '\n' ) );
            } );
            errors = arrayErrors.join( '\n' );
          } else if ( _.isArray( data.errors ) ) {
            errors = data.errors.join( '\n' );
          } else {
            errors = data.errors;
          }
        } else if ( data.message ) {
          errors = data.message;
        } else if ( data.Message ) {
          errors = data.Message;
        } else if ( data.error_description ) {
          errors = data.error_description;
        } else {
          errors = data;
        }
      }

      if ( !errors || _.isEmpty( errors ) ) {
        errors = 'Se produjo un error al intentar conectarse al servidor';
      }

      if ( config?.method === 'get' && !_.isString( errors ) ) {
        ( response as any ).errors = 'Se produjo un error al intentar conectarse al servidor';
      } else {
        ( response as any ).errors = errors;
      }
    }
  } );

  return api;
};

export default buildBaseApi;
